import { useEffect, useState } from "react";
import { BarChart3, TrendingUp, PieChart as PieIcon } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area, PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "@/lib/api";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtMonth = (k) => {
  const [y, m] = k.split("-");
  return `${MONTHS[Number(m) - 1]} '${y.slice(2)}`;
};
const PIE_COLORS = { active: "#059669", trial: "#d97706", expired: "#dc2626", deactivated: "#64748b" };
const PIE_LABELS = { active: "Paid · Active", trial: "On trial", expired: "Expired", deactivated: "Deactivated" };

export const PlatformAnalytics = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/platform/analytics").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;

  const months = data.months.map((m) => ({ ...m, label: fmtMonth(m.month) }));
  const pie = Object.entries(data.breakdown)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: PIE_LABELS[k], value: v, color: PIE_COLORS[k] }));
  const totalAccounts = Object.values(data.breakdown).reduce((a, b) => a + b, 0);

  return (
    <section className="mb-8" data-testid="platform-analytics">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-1" data-testid="analytics-revenue-chart">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="h-4 w-4 text-emerald-600" />
            <span className="font-display text-sm font-semibold text-slate-900">Revenue by month</span>
          </div>
          <div className="text-xs text-slate-500 mb-3">Paid Stripe checkouts, split by plan.</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={months} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(v, n) => [`$${Number(v).toFixed(2)}`, n]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="revenue_monthly" name="Monthly plans" stackId="rev" fill="#059669" radius={[0, 0, 0, 0]} />
              <Bar dataKey="revenue_annual" name="Annual plans" stackId="rev" fill="#2563eb" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-1" data-testid="analytics-signups-chart">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="h-4 w-4 text-primary" />
            <span className="font-display text-sm font-semibold text-slate-900">New accounts by month</span>
          </div>
          <div className="text-xs text-slate-500 mb-3">Companies registered per month.</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={months} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
              <defs>
                <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0f172a" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#0f172a" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Area type="monotone" dataKey="signups" name="Signups" stroke="#0f172a" strokeWidth={2} fill="url(#signupGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-1" data-testid="analytics-subscriptions-chart">
          <div className="flex items-center gap-2 mb-1">
            <PieIcon className="h-4 w-4 text-amber-600" />
            <span className="font-display text-sm font-semibold text-slate-900">Subscription status</span>
          </div>
          <div className="text-xs text-slate-500 mb-3">{totalAccounts} account{totalAccounts === 1 ? "" : "s"} by current status.</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pie} dataKey="value" nameKey="name" innerRadius={52} outerRadius={80} paddingAngle={3} strokeWidth={0}>
                {pie.map((p) => <Cell key={p.name} fill={p.color} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
};
